import { AfterViewInit, ChangeDetectionStrategy, Component, Injector, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TerminalTemplateTransferService } from '@shared/services/export-import-services/terminalTemplateTransfer.service';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { ITerminalTemplate, TerminalTemplateAdministrationService } from '@shared/services/terminaltemplateAdministration.service';
import { Subject, takeUntil } from 'rxjs';
import { CreateTerminalTemplateComponent } from './create-terminaltemplate/create-terminaltemplate.component';
import { DeleteTerminalTemplateComponent } from './delete-terminaltemplate/delete-terminaltemplate.component';
import { EditTerminalTemplateComponent } from './edit-terminaltemplate/edit-terminaltemplate.component';

interface ITableViewModelItem {
	alias: string;
	name: string;
	template: ITerminalTemplate;
}

@Component({
	selector: 'app-terminaltemplate-administration',
	templateUrl: './terminaltemplate-administration.component.html',
	styleUrls: ['./terminaltemplate-administration.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TerminalTemplateAdministrationComponent implements OnDestroy, AfterViewInit {
	private destroy$: Subject<void> = new Subject<void>();
	displayedColumns: string[] = ['name', 'menu'];
	dataSource = new MatTableDataSource<ITableViewModelItem>();
	showSearch = false;
	isLoading = true;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'mes.administration.terminaltemplate.export-to-excel',
			click: () => this.onExport(),
			permissions: () => ['mes:TerminalGet'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'mes.administration.terminaltemplate.import-from-excel',
			click: () => this.onImport(),
			permissions: () => ['mes:TerminalCreate', 'mes:TerminalUpdate', 'mes:TerminalGet'],
		},
		{
			icon: () => 'acies_export',
			text: () => 'mes.administration.terminaltemplate.download-template',
			// Driven by the transfer service's own capability query rather than a hardcoded
			// per-entity check, so an entity that offers no template loses the entry without a
			// component edit.
			disabled: () => !this.terminalTemplateTransferService.hasTemplate(),
			hideOnDisable: () => true,
			click: () => this.downloadTemplate(),
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.terminaltemplate.create-terminaltemplate',
			click: () => this.openCreateTerminalTemplate(),
			permissions: () => ['mes:TerminalCreate', 'mes:TerminalGet'],
			disableOnMissingPermissions: () => true,
		},
	];

	constructor(
		public dialog: MatDialog,
		private notificationService: NotificationService,
		private terminalTemplateAdministrationService: TerminalTemplateAdministrationService,
		private terminalTemplateTransferService: TerminalTemplateTransferService,
		private injector: Injector,
	) {}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	private getITableViewModelItem(template: ITerminalTemplate): ITableViewModelItem {
		return {
			alias: template.alias,
			name: template.name ? template.name : '',
			template,
		};
	}

	private getTableViewModel(templates: ITerminalTemplate[]): ITableViewModelItem[] {
		return templates.map((template) => this.getITableViewModelItem(template));
	}

	updateList() {
		this.isLoading = true;
		this.terminalTemplateAdministrationService.getTerminalTemplates().subscribe((c) => {
			this.dataSource.data = this.getTableViewModel(c.result);
			this.dataSource.sort = this.sort;
			this.onSort(this.sort);
			this.isLoading = false;
		});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	openCreateTerminalTemplate() {
		const dialogRef = this.dialog.open(CreateTerminalTemplateComponent, {
			disableClose: true,
			width: '1000px',
			data: null,
		});
		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	openEditTerminalTemplate(terminalTemplate: ITerminalTemplate | null) {
		const dialogRef = this.dialog.open(EditTerminalTemplateComponent, {
			disableClose: true,
			width: '1000px',
			data: terminalTemplate,
		});
		dialogRef.componentInstance.edited.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	openDeleteTerminalTemplate(terminalTemplate: ITerminalTemplate) {
		const dialogRef = this.dialog.open(DeleteTerminalTemplateComponent, {
			disableClose: true,
			width: '450px',
			data: terminalTemplate,
		});
		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.notificationService.notify(['mes.administration.terminaltemplate.delete.notification.header'], ['mes.administration.terminaltemplate.delete.notification.content', { alias: terminalTemplate.alias }], 'success');
			this.updateList();
		});
	}

	onExport() {
		// The shared contract exports the caller's in-memory rows rather than re-fetching, the same
		// way DepartmentComponent does.
		const templates = this.dataSource.data.map((item) => item.template);

		this.terminalTemplateTransferService
			.exportData(templates)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.notificationService.notify(['mes.administration.terminaltemplate.export.notification.header'], ['mes.administration.terminaltemplate.export.notification.content'], 'success');
				},
				error: () => {
					this.notificationService.notify(['mes.administration.terminaltemplate.export.error.header'], ['mes.administration.terminaltemplate.export.error.content'], 'error');
				},
			});
	}

	onImport() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'mes.administration.terminaltemplate.import.title',
				subHeadingKey: 'mes.administration.terminaltemplate.import.subtitle',
				confirmButtonKey: 'mes.administration.terminaltemplate.import.button',
				notificationHeader: 'mes.administration.terminaltemplate.import.notification.success.header',
				notificationContent: 'mes.administration.terminaltemplate.import.notification.success.content',
				notificationErrorHeader: 'mes.administration.terminaltemplate.import.notification.error.header',
				notificationErrorContent: 'mes.administration.terminaltemplate.import.notification.error.content',
				notificationClientErrorHeader: 'mes.administration.terminaltemplate.import.notification.clientError.header',
				notificationClientErrorContent: 'mes.administration.terminaltemplate.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.terminalTemplateTransferService,
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'mes.administration.terminaltemplate.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		// The refresh used to live in this component's own import subscription. That subscription is
		// now owned by ImportValidatorService, so without this the table would show stale rows after
		// a successful import.
		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.updateList();
			});
	}

	downloadTemplate() {
		this.terminalTemplateTransferService.getTemplate();
		this.notificationService.notify(['mes.administration.terminaltemplate.template.notification.header'], ['mes.administration.terminaltemplate.template.notification.content'], 'success');
	}
}
